/**
 * What an agent is called, and the entity every `agents_*` tool answers with.
 *
 * Scopes are deliberately **not** here. `apps/api/src/actions/vocab.ts` owns them,
 * because a scope is a property of an action first and of an agent second, and
 * `.docs/05-actions-mcp-agents.md` §DRY makes that file the single source: "Scope
 * markers, verb vocabulary and domain list are single constants in
 * `actions/vocab.ts`".
 *
 * Queues are not here either, any more. They arrived with session 05 because
 * `agent.queues` needed them, and session 07 moved them to `schemas/tasks.ts` — where
 * the thing they are a property of lives (`.docs/07-tasks.md` deliverable 1: "the
 * closed list … in `packages/shared/src/schemas/tasks.ts`").
 */
import { z } from 'zod';
import { GrantableScopeSchema } from './actions.js';
import { MoneySchema, Rfc3339Schema } from './primitives.js';
import { TaskQueueSchema } from './tasks.js';
/**
 * What an agent is called. Not a ref — an agent is addressed by this name in every
 * `agents_*` tool, so it is short, lowercase and unambiguous at a glance in a list.
 */
export const AgentNameSchema = z
    .string()
    .trim()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'An agent name is lowercase letters, digits, hyphens and underscores.')
    .describe('What this agent is called — lowercase, hyphenated, unique: "content-drafter", ' +
    '"wise-matcher". It is how every agents_* tool addresses it.');
/* ------------------------------------------------------------------ the entity */
/**
 * An agent as every tool answers with it — and never its token or either hash.
 *
 * `.docs/00-index.md` §5 puts entity schemas here so the API validates and session
 * 20's form resolves against the same object. It is also the output schema of five
 * `agents_*` actions, which means the registry parses a real answer against it on
 * every call the integration suite makes.
 */
export const AgentSchema = z
    .object({
    name: AgentNameSchema,
    owner_user_id: z
        .string()
        .describe('The staff user who created this agent and reviews what it may not do.'),
    scopes: z
        .array(GrantableScopeSchema)
        .describe('What it is allowed to do. In scope order, so two agents alike read alike.'),
    queues: z
        .array(TaskQueueSchema)
        .describe('Which queues it pulls work from. Empty means nothing is routed to it.'),
    money_limit: MoneySchema.describe('The ceiling a $ tool is measured against. Zero — the default — means every money ' +
        'action becomes a review task rather than running.'),
    daily_call_budget: z
        .int()
        .describe('How many tool calls it may make in one UTC day before it is rate limited.'),
    calls_today: z.int().describe('How many it has made today. Resets at midnight UTC.'),
    token_prefix: z
        .string()
        .describe('The public half of its current token — what a log line names it by.'),
    previous_token_expires_at: Rfc3339Schema.nullable().describe('When the token replaced by the last rotation stops working, or null if there is none.'),
    last_seen_at: Rfc3339Schema.nullable().describe('When it last made a call, or null.'),
    revoked_at: Rfc3339Schema.nullable().describe('When it was revoked, or null while it still works.'),
    created_at: Rfc3339Schema.describe('When it was created.'),
})
    .describe('One agent principal: who owns it, what it may do, and how much of it it has used.');
//# sourceMappingURL=agents.js.map